import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Settings, Save, RefreshCw, Sparkles, FileText, AlertCircle, Building2, Briefcase, Plus, Trash2, Eye, Image, Upload, Wrench, UserCog, Download, Database, HardDrive, FileUp, Check, X, Shield, ChevronDown, ChevronUp } from "lucide-react";

export default function AdminSettings({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { hasPermission } = useRBAC();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true);
  const [autoReplyMode, setAutoReplyMode] = useState("ai");
  const [autoReplyTemplate, setAutoReplyTemplate] = useState("");
  const [requireTrustedDomain, setRequireTrustedDomain] = useState(false);
  const [trustedDomains, setTrustedDomains] = useState("niello.com");

  // Use effective role for permission checks
  const effectiveRole = previewRole || user.role;
  
  // Dynamic RBAC permission checks
  const canViewUsers = hasPermission('settings_users', effectiveRole);
  const canViewConnections = hasPermission('settings_connections', effectiveRole);
  const canViewSiteSettings = hasPermission('settings_site', effectiveRole);
  
  // Super Admin always has full edit access, others based on their view permissions
  const canEdit = effectiveRole === "super_admin" || effectiveRole === "admin";

  // Dealership & Department Management
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newDealership, setNewDealership] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [addingDealership, setAddingDealership] = useState(false);
  const [addingDepartment, setAddingDepartment] = useState(false);

  // Branding
  const [branding, setBranding] = useState({ logo_url: "/niello-logo.png", site_name: "Niello HelpDesk" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [siteName, setSiteName] = useState("Niello HelpDesk");
  const fileInputRef = useRef(null);

  // Technician Management
  const [technicians, setTechnicians] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addingTechnician, setAddingTechnician] = useState(false);

  // Backup & Export
  const [backupStats, setBackupStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const importFileRef = useRef(null);

  // RBAC Section
  const [rbacExpanded, setRbacExpanded] = useState(false);
  const [rbacSettings, setRbacSettings] = useState(null);
  const [rbacLoading, setRbacLoading] = useState(false);
  const [rbacSaving, setRbacSaving] = useState(false);
  const [rbacModified, setRbacModified] = useState(false);

  // File Hosting
  const [hostedFiles, setHostedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileDescription, setFileDescription] = useState("");
  const [fileIsPublic, setFileIsPublic] = useState(true);
  const hostedFileRef = useRef(null);
  
  // RBAC feature mapping for display
  const rbacFeatureConfig = [
    { category: "Dashboard", items: [
      { key: "dashboard_view", name: "View Dashboard", allowScoped: false },
      { key: "bulletin_post", name: "Post Bulletins", allowScoped: false },
      { key: "bulletin_view", name: "View Bulletins", allowScoped: false },
    ]},
    { category: "IT Tickets", items: [
      { key: "ticket_view", name: "View IT Tickets", allowScoped: true },
      { key: "ticket_create", name: "Create IT Tickets", allowScoped: false },
      { key: "ticket_update", name: "Update IT Tickets", allowScoped: false },
      { key: "ticket_comment", name: "Add Comments", allowScoped: false },
      { key: "ticket_private_comment", name: "Private Comments", allowScoped: false },
      { key: "ticket_assign", name: "Assign Tickets", allowScoped: false },
      { key: "credentials_send", name: "Send Credentials", allowScoped: false },
    ]},
    { category: "HR Requests", items: [
      { key: "hr_view", name: "View HR Board", allowScoped: true },
      { key: "hr_create", name: "Create HR Requests", allowScoped: false },
      { key: "hr_update", name: "Update HR Requests", allowScoped: false },
    ]},
    { category: "Ticket History", items: [
      { key: "history_view", name: "View Ticket History", allowScoped: true },
    ]},
    { category: "Analytics", items: [
      { key: "analytics_view", name: "View Analytics", allowScoped: false },
    ]},
    { category: "Settings", items: [
      { key: "settings_users", name: "User Admin", allowScoped: false },
      { key: "settings_connections", name: "Connections", allowScoped: false },
      { key: "settings_site", name: "Site Management", allowScoped: false },
    ]},
    { category: "View Testing", items: [
      { key: "view_testing", name: "Preview as Other Roles", allowScoped: false },
    ]},
  ];
  
  const roleKeys = ["super_admin", "admin", "site_admin", "department_admin", "end_user"];
  const roleLabels = {"super_admin": "Super Admin", "admin": "Admin", "site_admin": "Site Admin", "department_admin": "Dept Admin", "end_user": "End User"};
  
  // Fetch RBAC settings
  const fetchRbacSettings = async () => {
    try {
      setRbacLoading(true);
      const response = await axios.get(`${API}/admin/rbac`);
      setRbacSettings(response.data);
    } catch (e) { /* non-critical */ } finally {
      setRbacLoading(false);
    }
  };
  
  // Save RBAC settings
  const saveRbacSettings = async () => {
    try {
      setRbacSaving(true);
      await axios.put(`${API}/admin/rbac`, rbacSettings);
      toast.success("RBAC permissions saved successfully");
      setRbacModified(false);
    } catch (error) {
      toast.error("Failed to save RBAC settings");
    } finally {
      setRbacSaving(false);
    }
  };
  
  // Toggle permission
  const togglePermission = (permKey, role) => {
    if (!rbacSettings) return;
    
    const currentValue = rbacSettings.permissions[permKey]?.[role];
    let newValue;
    
    // Cycle through values: true -> false -> (scoped options if allowed)
    if (currentValue === true) {
      newValue = false;
    } else if (currentValue === false) {
      newValue = true;
    } else if (currentValue === "all") {
      newValue = "dealership";
    } else if (currentValue === "dealership") {
      newValue = "own";
    } else if (currentValue === "own") {
      newValue = "conditional";
    } else if (currentValue === "conditional") {
      newValue = "all";
    } else {
      newValue = true;
    }
    
    setRbacSettings(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: {
          ...prev.permissions[permKey],
          [role]: newValue
        }
      }
    }));
    setRbacModified(true);
  };
  
  // Get display value for permission
  const getPermissionDisplay = (value) => {
    if (value === true) return { text: "✅", class: "text-green-600 text-lg" };
    if (value === false) return { text: "❌", class: "text-red-500 text-lg" };
    if (value === "all") return { text: "All", class: "text-blue-600 font-medium text-xs" };
    if (value === "dealership") return { text: "Dealership", class: "text-blue-600 font-medium text-xs" };
    if (value === "own") return { text: "Own Only", class: "text-blue-600 font-medium text-xs" };
    if (value === "conditional") return { text: "Conditional", class: "text-amber-600 font-medium text-xs" };
    return { text: String(value), class: "text-slate-600" };
  };
  
  // Update privileged department
  const togglePrivilegedDept = (dept) => {
    if (!rbacSettings) return;
    const current = rbacSettings.privileged_departments || [];
    const updated = current.includes(dept) 
      ? current.filter(d => d !== dept)
      : [...current, dept];
    setRbacSettings(prev => ({
      ...prev,
      privileged_departments: updated
    }));
    setRbacModified(true);
  };

  useEffect(() => {
    fetchSettings();
    fetchDealerships();
    fetchDepartments();
    fetchBranding();
    fetchTechnicians();
    fetchAllUsers();
    if (user.role === "super_admin" || user.role === "admin") {
      fetchBackupStats();
    }
    if (user.role === "super_admin") {
      fetchRbacSettings();
      fetchHostedFiles();
    }
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      setAutoReplyEnabled(response.data.auto_reply_enabled);
      setAutoReplyMode(response.data.auto_reply_mode);
      setAutoReplyTemplate(response.data.auto_reply_template);
      setRequireTrustedDomain(response.data.require_trusted_domain || false);
      setTrustedDomains((response.data.trusted_domains || ["niello.com"]).join(", "));
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchDealerships = async () => {
    try {
      const response = await axios.get(`${API}/admin/dealerships`);
      setDealerships(response.data);
    } catch (e) { /* non-critical */ }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/admin/departments`);
      setDepartments(response.data);
    } catch (e) { /* non-critical */ }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/admin/branding`);
      setBranding(response.data);
      setSiteName(response.data.site_name || "Niello HelpDesk");
    } catch (e) { /* non-critical */ }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/tickets/assignable-users`);
      setTechnicians(response.data);
    } catch (e) { /* non-critical */ }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Filter to show only users who are not already IT staff, admin, or super_admin
      const nonTechUsers = response.data.filter(
        user => user.role === 'end_user' || user.role === 'site_admin'
      );
      setAllUsers(nonTechUsers);
    } catch (e) { /* non-critical */ }
  };

  const handleAddTechnician = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    
    try {
      setAddingTechnician(true);
      await axios.patch(`${API}/admin/users/${selectedUserId}/role`, { role: "department_admin" });
      toast.success("User promoted to Technician successfully");
      setSelectedUserId("");
      fetchTechnicians();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add technician");
    } finally {
      setAddingTechnician(false);
    }
  };

  const handleRemoveTechnician = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove "${userName}" as a technician? They will be changed to an End User.`)) return;
    
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: "end_user" });
      toast.success("Technician removed successfully");
      fetchTechnicians();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove technician");
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      super_admin: "bg-purple-100 text-purple-700",
      admin: "bg-blue-100 text-blue-700",
      department_admin: "bg-green-100 text-green-700"
    };
    return classes[role] || "bg-gray-100 text-gray-700";
  };

  const getRoleDisplayName = (role) => {
    const names = {
      super_admin: "Super Admin",
      admin: "Admin",
      department_admin: "Department Admin"
    };
    return names[role] || role;
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: PNG, JPEG, GIF, WebP, SVG");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/branding/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Logo uploaded successfully");
      setBranding({ ...branding, logo_url: response.data.logo_url });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSaveSiteName = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/admin/branding`, { site_name: siteName });
      toast.success("Site name updated successfully");
      fetchBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update site name");
    } finally {
      setSaving(false);
    }
  };

  const handleAddDealership = async () => {
    if (!newDealership.trim()) {
      toast.error("Please enter a dealership name");
      return;
    }
    
    try {
      setAddingDealership(true);
      await axios.post(`${API}/admin/dealerships`, { name: newDealership.trim() });
      toast.success("Dealership added successfully");
      setNewDealership("");
      fetchDealerships();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add dealership");
    } finally {
      setAddingDealership(false);
    }
  };

  const handleDeleteDealership = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/dealerships/${id}`);
      toast.success("Dealership deleted");
      fetchDealerships();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete dealership");
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) {
      toast.error("Please enter a department name");
      return;
    }
    
    try {
      setAddingDepartment(true);
      await axios.post(`${API}/admin/departments`, { name: newDepartment.trim() });
      toast.success("Department added successfully");
      setNewDepartment("");
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add department");
    } finally {
      setAddingDepartment(false);
    }
  };

  const handleDeleteDepartment = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/departments/${id}`);
      toast.success("Department deleted");
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete department");
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Parse trusted domains from comma-separated string
      const domainsArray = trustedDomains
        .split(",")
        .map(d => d.trim())
        .filter(d => d.length > 0);
      
      await axios.patch(`${API}/admin/settings`, {
        auto_reply_enabled: autoReplyEnabled,
        auto_reply_mode: autoReplyMode,
        auto_reply_template: autoReplyTemplate,
        require_trusted_domain: requireTrustedDomain,
        trusted_domains: domainsArray
      });
      toast.success("Settings saved successfully!");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setAutoReplyEnabled(settings.auto_reply_enabled);
      setAutoReplyMode(settings.auto_reply_mode);
      setAutoReplyTemplate(settings.auto_reply_template);
      setRequireTrustedDomain(settings.require_trusted_domain || false);
      setTrustedDomains((settings.trusted_domains || ["niello.com"]).join(", "));
      toast.info("Reset to saved values");
    }
  };

  // Backup & Export Functions
  const fetchBackupStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/stats`);
      setBackupStats(response.data);
    } catch (e) { /* non-critical */ }
  };

  const fetchHostedFiles = async () => {
    try {
      const response = await axios.get(`${API}/hosted-files`);
      setHostedFiles(response.data);
    } catch (e) { /* non-critical */ }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit");
      return;
    }
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("is_public", fileIsPublic);
      formData.append("description", fileDescription);
      await axios.post(`${API}/hosted-files`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded");
      setFileDescription("");
      fetchHostedFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploadingFile(false);
      if (hostedFileRef.current) hostedFileRef.current.value = "";
    }
  };

  const handleDeleteHostedFile = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/hosted-files/${fileId}`);
      toast.success("File deleted");
      fetchHostedFiles();
    } catch (err) {
      toast.error("Failed to delete file");
    }
  };

  const handleToggleFileVisibility = async (fileId, currentPublic) => {
    try {
      await axios.patch(`${API}/hosted-files/${fileId}`, { is_public: !currentPublic });
      toast.success(`File is now ${!currentPublic ? "public" : "private"}`);
      fetchHostedFiles();
    } catch (err) {
      toast.error("Failed to update file");
    }
  };

  const copyFileUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    try {
      const textarea = document.createElement("textarea");
      textarea.value = fullUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("URL copied to clipboard");
    } catch (e) {
      // Fallback: prompt user to copy manually
      window.prompt("Copy this URL:", fullUrl);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };



  const handleExportBackup = async (format = "json") => {
    try {
      setExporting(true);
      const endpoint = format === "csv" 
        ? `${API}/admin/backup/export/tickets-csv`
        : `${API}/admin/backup/export`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = format === "csv" ? "niello_tickets.csv" : "niello_backup.json";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Backup exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export backup");
    } finally {
      setExporting(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error("Only JSON files are supported for import");
      return;
    }
    
    if (!window.confirm("Are you sure you want to import this backup? Existing data with matching IDs will be merged (not replaced).")) {
      event.target.value = '';
      return;
    }
    
    try {
      setImporting(true);
      setImportResults(null);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_tickets', 'true');
      formData.append('import_users', 'false');
      formData.append('import_settings', 'true');
      formData.append('import_bulletins', 'true');
      formData.append('merge_mode', 'true');
      
      const response = await axios.post(`${API}/admin/backup/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResults(response.data.results);
      toast.success("Import completed successfully");
      fetchBackupStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import backup");
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525]">
        <Navbar 
          user={user} 
          logout={logout} 
          effectiveRole={previewRole || user.role}
          previewRole={previewRole} 
          setPreviewRole={setPreviewRole}
          previewDealership={previewDealership}
          setPreviewDealership={setPreviewDealership}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-slate-60 dark:text-white0 dark:text-white">Loading settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525]">
      <Navbar 
        user={user} 
        logout={logout} 
        effectiveRole={previewRole || user.role}
        previewRole={previewRole} 
        setPreviewRole={setPreviewRole}
        previewDealership={previewDealership}
        setPreviewDealership={setPreviewDealership}
      />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-[#7a1d42]" />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Site Management</h1>
          </div>
          <p className="text-slate-600 dark:text-white">
            {canEdit ? "Configure system-wide settings and preferences" : `Viewing settings for ${user.dealership || "your site"}`}
          </p>
        </div>

        {/* View Only Notice for Site Admins */}
        {!canEdit && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>View Only:</strong> As a Department Admin, you can view site settings but cannot modify them. Contact a Super Admin or Admin to make changes.
              </p>
            </div>
          </div>
        )}

        {/* Site Management Section - Requires settings_site permission */}
        {canViewSiteSettings && (
        <>
        {/* Branding Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-[#7a1d42]" />
              Branding
            </CardTitle>
            <CardDescription>
              Customize your site logo and branding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo Upload */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Site Logo</Label>
              <div className="flex items-start gap-6">
                {/* Current Logo Preview */}
                <div className="flex-shrink-0">
                  <div className="border rounded-lg p-4 bg-slate-50 dark:bg-[#343434]">
                    <img 
                      src={branding.logo_url?.startsWith('/api') ? `${API.replace('/api', '')}${branding.logo_url}` : branding.logo_url} 
                      alt="Current Logo" 
                      className="h-16 w-auto object-contain"
                      onError={(e) => { e.target.src = "/niello-logo.png"; }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-white mt-2 text-center">Current Logo</p>
                </div>

                {/* Upload Section */}
                <div className="flex-1 space-y-3">
                  <div className="border-2 border-dashed border-slate-300 dark:border-[#3D3D3D] rounded-lg p-6 text-center hover:border-[#7a1d42] transition-colors">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      id="logo-upload"
                      disabled={!canEdit}
                    />
                    <label htmlFor="logo-upload" className={`${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                      <Upload className="w-8 h-8 mx-auto text-slate-400 dark:text-white mb-2" />
                      <p className="text-sm font-medium text-slate-700 dark:text-white">
                        {uploadingLogo ? "Uploading..." : canEdit ? "Click to upload new logo" : "Logo upload disabled"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-white mt-1">
                        PNG, JPEG, GIF, WebP or SVG (max 5MB)
                      </p>
                    </label>
                  </div>
                  {uploadingLogo && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-white">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Uploading logo...
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Site Name */}
            <div className="space-y-2 pt-4 border-t">
              <Label htmlFor="site-name" className="text-base font-medium">Site Name</Label>
              <p className="text-sm text-slate-500 dark:text-white">
                The name displayed in the browser tab and throughout the application
              </p>
              <div className="flex gap-2">
                <Input
                  id="site-name"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Enter site name..."
                  className="flex-1"
                  readOnly={!canEdit}
                />
                {canEdit && (
                  <Button
                    onClick={handleSaveSiteName}
                    disabled={saving || siteName === branding.site_name}
                    className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-2">Save</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Last Updated Info */}
            {branding.updated_at && (
              <div className="text-xs text-slate-500 dark:text-white pt-2">
                Last updated by {branding.updated_by_name} on {new Date(branding.updated_at).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dealership Management Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#7a1d42]" />
              Dealership Management
            </CardTitle>
            <CardDescription>
              {canEdit ? "Manage the list of dealerships available in the system" : "View dealerships in the system"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* View existing dealerships dropdown */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                View Existing Dealerships
              </Label>
              <Select>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={`${dealerships.length} dealership(s) configured - Click to view`} />
                </SelectTrigger>
                <SelectContent>
                  {dealerships.length === 0 ? (
                    <SelectItem value="none" disabled>No dealerships configured</SelectItem>
                  ) : (
                    dealerships.map((dealership) => (
                      <SelectItem key={dealership.id} value={dealership.id}>
                        {dealership.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Add new dealership - only for admins */}
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  value={newDealership}
                  onChange={(e) => setNewDealership(e.target.value)}
                  placeholder="Enter dealership name..."
                  onKeyDown={(e) => e.key === "Enter" && handleAddDealership()}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddDealership}
                  disabled={addingDealership || !newDealership.trim()}
                  className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                >
                  {addingDealership ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span className="ml-2">Add</span>
                </Button>
              </div>
            )}

            {/* Dealership list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {dealerships.length === 0 ? (
                <div className="p-4 text-center text-slate-50 dark:text-white0">
                  No dealerships configured. Add your first dealership above.
                </div>
              ) : (
                dealerships.map((dealership) => (
                  <div key={dealership.id} className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-800">
                    <span className="text-slate-90 dark:text-white0 dark:text-white">{dealership.name}</span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDealership(dealership.id, dealership.name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-slate-50 dark:text-white0">{dealerships.length} dealership(s) configured</p>
          </CardContent>
        </Card>

        {/* Department Management Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#7a1d42]" />
              Department Management
            </CardTitle>
            <CardDescription>
              {canEdit ? "Manage the list of departments available in the system" : "View departments in the system"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* View existing departments dropdown */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                View Existing Departments
              </Label>
              <Select>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={`${departments.length} department(s) configured - Click to view`} />
                </SelectTrigger>
                <SelectContent>
                  {departments.length === 0 ? (
                    <SelectItem value="none" disabled>No departments configured</SelectItem>
                  ) : (
                    departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Add new department - only for admins */}
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  placeholder="Enter department name..."
                  onKeyDown={(e) => e.key === "Enter" && handleAddDepartment()}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddDepartment}
                  disabled={addingDepartment || !newDepartment.trim()}
                  className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                >
                  {addingDepartment ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span className="ml-2">Add</span>
                </Button>
              </div>
            )}

            {/* Department list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {departments.length === 0 ? (
                <div className="p-4 text-center text-slate-50 dark:text-white0">
                  No departments configured.
                </div>
              ) : (
                departments.map((department) => (
                  <div key={department.id} className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-800">
                    <span className="text-slate-90 dark:text-white0 dark:text-white">{department.name}</span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDepartment(department.id, department.name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-slate-50 dark:text-white0">{departments.length} department(s) configured</p>
          </CardContent>
        </Card>
        </>
        )}
        {/* File Hosting - Super Admin Only */}
        {user.role === "super_admin" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-[#7a1d42]" />
              File Hosting
            </CardTitle>
            <CardDescription>
              Upload files to host on the site. Public files are accessible via URL without login.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Section */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  placeholder="e.g., Company logo, IT policy document"
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  data-testid="hosted-file-description"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={fileIsPublic}
                    onCheckedChange={setFileIsPublic}
                    data-testid="hosted-file-public-toggle"
                  />
                  <Label className="text-sm">{fileIsPublic ? "Public" : "Private"}</Label>
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    ref={hostedFileRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="hosted-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => hostedFileRef.current?.click()}
                    disabled={uploadingFile}
                    data-testid="hosted-file-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingFile ? "Uploading..." : "Choose & Upload File"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">Max 10 MB per file. Any file type accepted.</p>
            </div>

            {/* File List */}
            {hostedFiles.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Hosted Files ({hostedFiles.length})</Label>
                <div className="divide-y divide-slate-200 dark:divide-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  {hostedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-800 dark:text-white truncate">{file.filename}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${file.is_public ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {file.is_public ? "Public" : "Private"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-500">{formatFileSize(file.file_size)}</span>
                          {file.description && <span className="text-xs text-slate-400 truncate">{file.description}</span>}
                          <span className="text-xs text-slate-400">{new Date(file.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyFileUrl(file.url)}
                          title="Copy URL"
                          className="h-8 px-2"
                          data-testid={`copy-url-${file.id}`}
                        >
                          <FileUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFileVisibility(file.id, file.is_public)}
                          title={file.is_public ? "Make Private" : "Make Public"}
                          className="h-8 px-2"
                          data-testid={`toggle-vis-${file.id}`}
                        >
                          {file.is_public ? <Eye className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteHostedFile(file.id, file.filename)}
                          className="h-8 px-2 text-red-500 hover:text-red-700"
                          title="Delete"
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4 text-sm">No hosted files yet</p>
            )}
          </CardContent>
        </Card>
        )}

        {/* End Site Management Section */}

        {/* Auto-Reply Settings Card - only for admins with site settings permission */}
        {canEdit && canViewSiteSettings && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#7a1d42]" />
              Auto-Reply Settings
            </CardTitle>
            <CardDescription>
              Configure automatic responses when tickets are created by end users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Auto-Reply */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="auto-reply-enabled" className="text-base font-medium">
                  Enable Auto-Reply
                </Label>
                <p className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                  Automatically send a response when tickets are created
                </p>
              </div>
              <Switch
                id="auto-reply-enabled"
                checked={autoReplyEnabled}
                onCheckedChange={setAutoReplyEnabled}
              />
            </div>

            {/* Mode Selection */}
            {autoReplyEnabled && (
              <>
                <div className="space-y-3">
                  <Label className="text-base font-medium">Response Mode</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* AI Mode */}
                    <button
                      onClick={() => setAutoReplyMode("ai")}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        autoReplyMode === "ai"
                          ? "border-[#7a1d42] bg-[#7a1d42]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Sparkles className={`w-5 h-5 mt-0.5 ${autoReplyMode === "ai" ? "text-[#7a1d42]" : "text-slate-40 dark:text-white0"}`} />
                        <div>
                          <div className="font-medium text-slate-90 dark:text-white0 dark:text-white">AI-Generated</div>
                          <div className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                            Use AI to generate contextual responses based on ticket content
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Template Mode */}
                    <button
                      onClick={() => setAutoReplyMode("template")}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        autoReplyMode === "template"
                          ? "border-[#7a1d42] bg-[#7a1d42]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <FileText className={`w-5 h-5 mt-0.5 ${autoReplyMode === "template" ? "text-[#7a1d42]" : "text-slate-40 dark:text-white0"}`} />
                        <div>
                          <div className="font-medium text-slate-90 dark:text-white0 dark:text-white">Custom Template</div>
                          <div className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                            Use a fixed message for all tickets
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Template Editor (shown for both modes) */}
                <div className="space-y-2">
                  <Label htmlFor="template" className="text-base font-medium">
                    {autoReplyMode === "ai" ? "Fallback Message" : "Auto-Reply Message"}
                  </Label>
                  <p className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                    {autoReplyMode === "ai" 
                      ? "This message will be used if AI generation fails or is unavailable"
                      : "This message will be sent to users when they create a ticket"
                    }
                  </p>
                  <Textarea
                    id="template"
                    value={autoReplyTemplate}
                    onChange={(e) => setAutoReplyTemplate(e.target.value)}
                    rows={4}
                    className="resize-none"
                    placeholder="Enter your auto-reply message..."
                  />
                </div>

                {/* Preview */}
                {autoReplyTemplate && (
                  <div className="p-4 bg-slate-50 dark:bg-[#2C2C2C] rounded-lg border">
                    <div className="text-sm font-medium text-slate-70 dark:text-white0 dark:text-white mb-2">
                      Preview
                    </div>
                    <div className="text-sm text-slate-60 dark:text-white0 dark:text-white whitespace-pre-wrap">
                      {autoReplyTemplate}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Trusted Domain Settings Card - Requires site settings permission */}
        {canEdit && canViewSiteSettings && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#7a1d42]" />
              Registration Domain Restrictions
            </CardTitle>
            <CardDescription>
              Control which email domains can register new accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Domain Restriction */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="require-trusted-domain" className="text-base font-medium">
                  Require Trusted Domain
                </Label>
                <p className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                  Only allow users with approved email domains to register
                </p>
              </div>
              <Switch
                id="require-trusted-domain"
                checked={requireTrustedDomain}
                onCheckedChange={setRequireTrustedDomain}
              />
            </div>

            {/* Trusted Domains Input */}
            {requireTrustedDomain && (
              <div className="space-y-2">
                <Label htmlFor="trusted-domains" className="text-base font-medium">
                  Trusted Domains
                </Label>
                <p className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                  Enter email domains separated by commas (e.g., niello.com, example.com)
                </p>
                <Input
                  id="trusted-domains"
                  value={trustedDomains}
                  onChange={(e) => setTrustedDomains(e.target.value)}
                  placeholder="niello.com, example.com"
                  className="font-mono"
                />
                {trustedDomains && (
                  <div className="p-3 bg-slate-50 dark:bg-[#2C2C2C] rounded-lg border">
                    <div className="text-sm font-medium text-slate-70 dark:text-white0 dark:text-white mb-2">
                      Allowed Domains:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {trustedDomains.split(",").map((domain, i) => (
                        domain.trim() && (
                          <span key={domain.trim()} className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-medium">
                            @{domain.trim()}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Warning Message */}
            {requireTrustedDomain && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium mb-1">Domain Restriction Active</p>
                    <p>Users with email addresses outside the trusted domains will not be able to register new accounts. Existing users will not be affected.</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Backup & Export Card - Super Admin with site settings permission */}
        {effectiveRole === "super_admin" && canViewSiteSettings && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-[#7a1d42]" />
              Backup & Data Management
            </CardTitle>
            <CardDescription>
              Export your data for backup or migration purposes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* System Health Monitor */}
            {backupStats && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">System Health Monitor</Label>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {backupStats.total_tickets.toLocaleString()} / 15,000 tickets
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-4 rounded-full transition-all duration-500 ${
                        backupStats.total_tickets >= 15000 
                          ? 'bg-red-500' 
                          : backupStats.total_tickets >= 12000 
                            ? 'bg-orange-500' 
                            : backupStats.total_tickets >= 10000 
                              ? 'bg-yellow-500' 
                              : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min((backupStats.total_tickets / 15000) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>0</span>
                    <span>10K</span>
                    <span>15K</span>
                  </div>
                </div>

                {/* Warning Messages */}
                {backupStats.total_tickets >= 15000 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800 dark:text-red-200">Optimization Recommended</p>
                        <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                          You have reached {backupStats.total_tickets.toLocaleString()} tickets. Consider implementing server-side pagination 
                          or archiving old tickets to maintain optimal performance.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {backupStats.total_tickets >= 12000 && backupStats.total_tickets < 15000 && (
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-800 dark:text-orange-200">Approaching Optimization Threshold</p>
                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                          You have {backupStats.total_tickets.toLocaleString()} tickets. At 15,000 tickets, consider optimization.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {backupStats.total_tickets < 12000 && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800 dark:text-green-200">System Health: Excellent</p>
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          Current ticket volume is well within optimal performance range.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t pt-4"></div>

            {/* Database Stats */}
            {backupStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-50 dark:bg-[#2C2C2C] rounded-lg border">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7a1d42]">{backupStats.total_tickets.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Tickets</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7a1d42]">{backupStats.total_users.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7a1d42]">{backupStats.total_bulletins.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Bulletins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7a1d42]">{backupStats.total_dealerships.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Dealerships</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#7a1d42]">{backupStats.total_departments.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Departments</div>
                </div>
              </div>
            )}

            {/* Export Section */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Export Data</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Download your data as a backup file. JSON includes all data, CSV is tickets only.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleExportBackup("json")}
                  disabled={exporting}
                  className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                >
                  {exporting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Full Backup (JSON)
                </Button>
                <Button
                  onClick={() => handleExportBackup("csv")}
                  disabled={exporting}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Tickets (CSV)
                </Button>
              </div>
            </div>

            {/* Import Section */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Import Data</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Restore data from a previous backup. Only JSON format is supported for import.
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={importFileRef}
                  onChange={handleImportBackup}
                  accept=".json"
                  className="hidden"
                  id="backup-import"
                />
                <Button
                  onClick={() => importFileRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                >
                  {importing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileUp className="w-4 h-4 mr-2" />
                  )}
                  {importing ? "Importing..." : "Import Backup"}
                </Button>
              </div>
              
              {/* Import Results */}
              {importResults && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800 dark:text-green-200">Import Complete</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-green-700 dark:text-green-300">Tickets</div>
                      <div className="text-green-600">
                        {importResults.tickets.imported} imported, {importResults.tickets.skipped} skipped
                      </div>
                    </div>
                    <div>
                      <div className="text-green-700 dark:text-green-300">Users</div>
                      <div className="text-green-600">
                        {importResults.users.imported} imported, {importResults.users.skipped} skipped
                      </div>
                    </div>
                    <div>
                      <div className="text-green-700 dark:text-green-300">Bulletins</div>
                      <div className="text-green-600">
                        {importResults.bulletins.imported} imported, {importResults.bulletins.skipped} skipped
                      </div>
                    </div>
                    <div>
                      <div className="text-green-700 dark:text-green-300">Settings</div>
                      <div className="text-green-600">
                        {importResults.settings.imported ? "Imported" : "Skipped"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex gap-3">
                <HardDrive className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Data Persistence</p>
                  <p>Your data is automatically preserved when you redeploy the application. Backups are recommended for additional safety and migration between environments.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Last Updated Info */}
        {settings && canEdit && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="text-sm text-slate-60 dark:text-white0 dark:text-white">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">Last updated by:</span>
                  <span>{settings.updated_by_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Updated at:</span>
                  <span>{new Date(settings.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* RBAC Permissions Chart - Super Admin Only */}
        {user.role === "super_admin" && (
          <Card className="border-0 shadow-md">
            <CardHeader className="cursor-pointer" onClick={() => setRbacExpanded(!rbacExpanded)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#7a1d42]" />
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      RBAC Permissions Chart
                      {rbacModified && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Unsaved Changes</span>}
                    </CardTitle>
                    <CardDescription>Click on permissions to modify • Role-Based Access Control</CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setRbacExpanded(!rbacExpanded); }}>
                  {rbacExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </Button>
              </div>
            </CardHeader>
            
            {rbacExpanded && (
              <CardContent className="space-y-6">
                {rbacLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Loading RBAC settings...</span>
                  </div>
                ) : rbacSettings ? (
                  <>
                    {/* Save Button */}
                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        <span className="font-medium">Click</span> on any permission cell to cycle through options
                      </div>
                      <Button
                        onClick={saveRbacSettings}
                        disabled={!rbacModified || rbacSaving}
                        className="bg-[#7a1d42] hover:bg-[#5a1531]"
                        size="sm"
                      >
                        {rbacSaving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Permissions
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Role Hierarchy */}
                    <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                      <h4 className="font-semibold text-sm mb-2">Role Hierarchy</h4>
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-medium">Super Admin</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">Admin</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-medium">Site Admin</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">Dept Admin</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">End User</span>
                      </div>
                    </div>

                    {/* Editable Permissions Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b dark:border-slate-700">
                            <th className="text-left py-3 px-2 font-semibold text-slate-600 dark:text-slate-300">Feature</th>
                            {roleKeys.map((role) => (
                              <th key={role} className="text-center py-3 px-2 font-semibold text-slate-600 dark:text-slate-300 min-w-[90px]">
                                {roleLabels[role]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rbacFeatureConfig.map((category) => (
                            <>
                              <tr key={category.category} className="bg-slate-100 dark:bg-slate-800">
                                <td colSpan={6} className="py-2 px-2 font-semibold text-[#7a1d42]">
                                  {category.category}
                                </td>
                              </tr>
                              {category.items.map((item) => (
                                <tr key={item.key} className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                  <td className="py-2 px-2 text-slate-700 dark:text-slate-300">{item.name}</td>
                                  {roleKeys.map((role) => {
                                    const value = rbacSettings.permissions[item.key]?.[role];
                                    const display = getPermissionDisplay(value);
                                    return (
                                      <td key={role} className="text-center py-2 px-2">
                                        <button
                                          onClick={() => togglePermission(item.key, role)}
                                          className={`px-2 py-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer ${display.class}`}
                                          title={`Click to change (current: ${value})`}
                                        >
                                          {display.text}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Privileged Departments - Editable */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <h4 className="font-semibold text-sm text-blue-700 dark:text-blue-400 mb-2">Privileged Departments (Enhanced Dept Admin Access)</h4>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                        Click to toggle. Dept Admins in these departments get elevated access to all tickets and HR requests.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {departments.map((dept) => {
                          const isPrivileged = rbacSettings.privileged_departments?.includes(dept.name);
                          return (
                            <button
                              key={dept.name}
                              onClick={() => togglePrivilegedDept(dept.name)}
                              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                isPrivileged 
                                  ? "bg-blue-600 text-white" 
                                  : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                              }`}
                            >
                              {isPrivileged && <Check className="w-3 h-3 inline mr-1" />}
                              {dept.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Permission Legend */}
                    <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                      <h4 className="font-semibold text-sm mb-3">Permission Values (click to cycle)</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-green-600 text-lg">✅</span>
                          <span className="text-slate-600 dark:text-slate-300">Full Access</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-red-500 text-lg">❌</span>
                          <span className="text-slate-600 dark:text-slate-300">No Access</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-blue-600 font-medium">All</span>
                          <span className="text-slate-600 dark:text-slate-300">All Records</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-blue-600 font-medium">Dealership</span>
                          <span className="text-slate-600 dark:text-slate-300">Location Scoped</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-blue-600 font-medium">Own Only</span>
                          <span className="text-slate-600 dark:text-slate-300">User's Records</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded">
                          <span className="text-amber-600 font-medium">Conditional</span>
                          <span className="text-slate-600 dark:text-slate-300">Dept Based</span>
                        </div>
                      </div>
                    </div>

                    {/* Last Updated */}
                    {rbacSettings.updated_at && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 text-right">
                        Last updated: {new Date(rbacSettings.updated_at).toLocaleString()}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    Failed to load RBAC settings
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Action Buttons - only for admins */}
        {canEdit && (
        <div className="flex gap-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
          <Button
            onClick={handleReset}
            variant="outline"
            disabled={saving}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}
